import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface CitationHighlight {
  page: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CitationContextType {
  highlight: CitationHighlight | null;
  highlightKey: number;
  showCitation: (citation: CitationHighlight) => void;
  clearCitation: () => void;
}

const CitationContext = createContext<CitationContextType>({
  highlight: null,
  highlightKey: 0,
  showCitation: () => {},
  clearCitation: () => {},
});

export function CitationProvider({ children }: { children: ReactNode }) {
  const [highlight, setHighlight] = useState<CitationHighlight | null>(null);
  const [highlightKey, setHighlightKey] = useState(0);

  const showCitation = useCallback((citation: CitationHighlight) => {
    setHighlight(citation);
    setHighlightKey((k) => k + 1);
  }, []);

  const clearCitation = useCallback(() => {
    setHighlight(null);
  }, []);

  return (
    <CitationContext.Provider value={{ highlight, highlightKey, showCitation, clearCitation }}>
      {children}
    </CitationContext.Provider>
  );
}

export function useCitation() {
  return useContext(CitationContext);
}

/**
 * Helper: find a citation for a field path from specific_doc_citations.
 * Returns the first citation entry's bbox or null.
 */
export function getCitationForField(
  citations: Record<string, unknown> | null | undefined,
  fieldPath: string,
): CitationHighlight | null {
  if (!citations) return null;

  // Try exact match first
  const entries = citations[fieldPath] as
    | Array<{ page: number; bbox: Record<string, number> }>
    | undefined;
  if (entries?.[0]) {
    const e = entries[0];
    return {
      page: e.page ?? (e.bbox as Record<string, number>).page ?? 1,
      left: e.bbox.left,
      top: e.bbox.top,
      width: e.bbox.width,
      height: e.bbox.height,
    };
  }

  // Try partial match (DB keys have "result." prefix)
  for (const [key, value] of Object.entries(citations)) {
    if (key.endsWith(fieldPath) || key.endsWith(`.${fieldPath}`)) {
      const arr = value as Array<{ page: number; bbox: Record<string, number> }>;
      if (arr?.[0]) {
        const e = arr[0];
        return {
          page: e.page ?? (e.bbox as Record<string, number>).page ?? 1,
          left: e.bbox.left,
          top: e.bbox.top,
          width: e.bbox.width,
          height: e.bbox.height,
        };
      }
    }
  }

  return null;
}
